import { parseFillBlankStem } from "@/components/exam-lock/exam-lock-delivery-types"
import {
  escapeLeadInHtml,
  isLeadInRichHtml,
} from "@/lib/lead-in-html"
import { STATUS_BADGE_TONE_CLASS } from "@exxatdesignux/ui/lib/status-badge-tints"

/** Canonical marker — matches exam-lock `parseFillBlankStem`. */
export const FILL_BLANK_MARKER = "{{blank}}"

const FILL_BLANK_MARKER_RE = /\{\{blank\}\}/gi
const LEGACY_NUMBERED_BLANK_RE = /\{\{\d+\}\}/g

/** Blue interactive chip in the fill-blank question editor. */
export const FILL_BLANK_CHIP_CLASS = [
  "mx-0.5 inline-flex h-6 cursor-pointer select-none items-center rounded-md border px-2",
  "text-xs font-medium tabular-nums align-middle transition-[box-shadow,filter]",
  STATUS_BADGE_TONE_CLASS.info,
  "hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
].join(" ")

export const FILL_BLANK_CHIP_HIGHLIGHT_CLASS =
  "ring-2 ring-status-badge-info-fg shadow-sm brightness-95"

/** Normalize legacy `{{1}}` markers to `{{blank}}`. */
export function normalizeFillBlankStem(leadIn: string): string {
  return leadIn.replace(LEGACY_NUMBERED_BLANK_RE, FILL_BLANK_MARKER)
}

export function isFillBlankRichHtml(value: string): boolean {
  return /<span[^>]*data-fill-blank/i.test(value) || isLeadInRichHtml(value)
}

function fillBlankChipHtml(label: number): string {
  return `<span data-fill-blank="${label}" contenteditable="false" class="${FILL_BLANK_CHIP_CLASS}">Blank ${label}</span> `
}

/** Plain `{{blank}}` stem or legacy import → TipTap HTML. */
export function fillBlankLeadInToEditorHtml(leadIn: string): string {
  if (!leadIn.trim()) return ""
  if (isFillBlankRichHtml(leadIn)) return leadIn

  const parts = parseFillBlankStem(normalizeFillBlankStem(leadIn))
  let inner = ""
  for (const part of parts) {
    if (part.kind === "text") {
      inner += escapeLeadInHtml(part.text).replace(/\n/g, "<br>")
    } else {
      inner += fillBlankChipHtml(part.blankIndex + 1)
    }
  }
  return inner ? `<p>${inner}</p>` : ""
}

export function countFillBlankChipsInHtml(html: string): number {
  if (!html.trim()) return 0
  if (!isFillBlankRichHtml(html)) {
    return (normalizeFillBlankStem(html).match(FILL_BLANK_MARKER_RE) ?? []).length
  }
  const doc = new DOMParser().parseFromString(html, "text/html")
  return doc.querySelectorAll("[data-fill-blank]").length
}

/** Delivery stem: plain text with `{{blank}}` markers. */
export function fillBlankStemForDelivery(leadIn: string): string {
  if (!leadIn.trim()) return ""
  if (!isFillBlankRichHtml(leadIn)) return normalizeFillBlankStem(leadIn)

  const doc = new DOMParser().parseFromString(leadIn, "text/html")
  let out = ""

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? ""
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as HTMLElement
    if (el.hasAttribute("data-fill-blank")) {
      out += FILL_BLANK_MARKER
      return
    }
    const tag = el.tagName
    if (tag === "BR") {
      out += "\n"
      return
    }
    if (tag === "P" || tag === "DIV") {
      if (out.length > 0 && !out.endsWith("\n")) out += "\n"
    }
    el.childNodes.forEach(walk)
    if (tag === "P" || tag === "DIV") {
      if (!out.endsWith("\n")) out += "\n"
    }
  }

  doc.body.childNodes.forEach(walk)
  return normalizeFillBlankStem(out.replace(/\n+$/, ""))
}

function renumberFillBlankChipsInDom(root: ParentNode): void {
  let i = 0
  root.querySelectorAll("[data-fill-blank]").forEach((node) => {
    i++
    const el = node as HTMLElement
    el.setAttribute("data-fill-blank", String(i))
    el.setAttribute("aria-label", `Blank ${i}`)
    el.textContent = `Blank ${i}`
  })
}

export function removeFillBlankChipFromHtml(html: string, blankIndex: number): string {
  if (!isFillBlankRichHtml(html)) {
    const normalized = normalizeFillBlankStem(html)
    let seen = 0
    return normalized.replace(FILL_BLANK_MARKER_RE, (match) => {
      if (seen === blankIndex) {
        seen++
        return ""
      }
      seen++
      return match
    })
  }

  const doc = new DOMParser().parseFromString(html, "text/html")
  const chips = Array.from(doc.querySelectorAll("[data-fill-blank]"))
  chips[blankIndex]?.remove()
  renumberFillBlankChipsInDom(doc.body)
  return doc.body.innerHTML
}

/** Append a blank chip to the end of rich HTML (programmatic insert). */
export function appendFillBlankChipToHtml(html: string): string {
  const nextLabel = countFillBlankChipsInHtml(html) + 1
  const chip = fillBlankChipHtml(nextLabel)
  const trimmed = html.trim()
  if (!trimmed || trimmed === "<p></p>") return `<p>${chip}</p>`
  if (/<\/p>\s*$/i.test(trimmed)) {
    return trimmed.replace(/<\/p>\s*$/i, ` ${chip}</p>`)
  }
  return `${trimmed} ${chip}`
}

/** Visible text length for validation (strip HTML + blank chips). */
export function fillBlankLeadInPlainTextLength(leadIn: string): number {
  if (!leadIn.trim()) return 0
  if (!isFillBlankRichHtml(leadIn)) {
    return normalizeFillBlankStem(leadIn).replace(/\{\{blank\}\}/gi, "").trim().length
  }
  const doc = new DOMParser().parseFromString(leadIn, "text/html")
  doc.querySelectorAll("[data-fill-blank]").forEach((el) => el.remove())
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim().length
}
